/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Topology Types
 * Defines the data structures for rendering DAS network topology
 */

import { IInventoryGraphNode, IInventoryGraphEdge, IRemoteEquipment } from "../../store/api/devices/types";

// Node types in the DAS hierarchy
export enum DasNodeType {
  SITE = "site",           // Root site/device
  HEU = "heu",            // Head End Unit (Chassis)
  OIU = "oiu",            // Optical Interface Unit (Chassis)
  IHU = "ihu",            // Indoor Head Unit (Chassis)
  ACM = "acm",            // ACM Module (defines chassis)
  RIM = "rim",            // Radio Interface Module
  OIM = "oim",            // Optical Interface Module
  OCH = "och",            // Optical Channel Module
  GX = "gx",              // GX Module
  FMM = "fmm",            // Fiber Matrix Module
  RAU = "rau",            // Remote Access Unit
  MRU = "mru",            // Mobile Remote Unit
  RXU = "rxu",            // RXU Module (child of RAU)
  FCM = "fcm",            // FCM Module
  MXU = "mxu",  
  DCU = "dcu" , 
  DEU = "deu" , 
  RIU = "riu" ,            // RIU 
  DXRU = "dxru",
  DXRU10G = "dxru10g"
}

// Status types from API
export enum DasNodeStatus {
  ONLINE = "online",
  MINOR = "minor",
  MAJOR = "major",
  CRITICAL = "critical",
  DISCONNECTED = "disconnected",
  UNKNOWN = "unknown",
  CLEAR = "clear",
}

// Position for layout
export interface NodePosition {
  x: number;
  y: number;
}

// Topology node with layout and rendering info
export interface TopologyNode {
  id: string;
  label: string;
  type: DasNodeType;
  equipmentType: string;
  status: DasNodeStatus;
  model?: string;
  ipAddress?: string;
  serialNumber?: string;
  managedObject?: string;
  position?: NodePosition;
  children: TopologyNode[];
  metadata?: Record<string, any>;
}

// Edge between nodes
export interface TopologyEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

// Transform state for pan/zoom
export interface Transform {
  x: number;
  y: number;
  scale: number;
}

// Configuration for node rendering
export interface NodeTypeConfig {
  color: string;
  shape: "rect" | "circle" | "roundedRect";
  width: number;
  height: number;
  radius?: number;
  glowColor?: string;
}

// Layout configuration
export interface LayoutConfig {
  xGap: number;
  yGap: number;
  nodeWidth: number;
  nodeHeight: number;
}

// Hierarchy builder interface - for extensibility
export interface HierarchyBuilder {
  buildHierarchy(nodes: IInventoryGraphNode[], edges: IInventoryGraphEdge[], rootId: string): TopologyNode | null;
  parseNodeType(node: IInventoryGraphNode): DasNodeType;
  parseStatus(status: string): DasNodeStatus;
  setEquipmentData(equipment: IRemoteEquipment[]): void;
}
